import React, { useCallback } from 'react'
import { Box, TextField, Typography } from '@mui/material'

import DataExplorer from '../../data-explorer/DataExplorer'
import { GenNode } from '../../../types/carbon/models/GenNode'
import { CarbonIngestRequest } from '../../../hooks/useIngestSources'

/**
 * The survey door: an open-ended variable in THIS job's case data becomes one
 * record per case.
 *
 * Re-homed here from `datasource/` when ingest detached from pipelines. It now
 * edits the INGEST request — a variable name and an optional case filter —
 * rather than a pipeline's `data_source.carbon` config, and it has no pipeline
 * callbacks: nothing downstream needs to know which variable was browsed, only
 * which one was asked for.
 *
 * The customer/job it browses is the one the fetch will read, always: the
 * endpoint takes its scope from the path and ignores anything a body claims.
 */

interface CarbonConfigProps {
  request: CarbonIngestRequest
  onChange: (request: CarbonIngestRequest) => void
  customer: string
  job: string
  disabled?: boolean
}

const CarbonConfig: React.FC<CarbonConfigProps> = ({
  request,
  onChange,
  customer,
  job,
  disabled = false,
}) => {
  const handleVariableChange = useCallback(
    (node: GenNode) => {
      onChange({ ...request, variable: node.value1 || '' })
    },
    [request, onChange],
  )

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
      data-testid="autocoder-carbon-config"
    >
      <Typography variant="subtitle2" sx={{ mb: 1, flexShrink: 0 }}>
        Choose the variable to import
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DataExplorer
          customer={customer}
          job={job}
          initialVariableName={request.variable || undefined}
          readOnly={disabled}
          onVariableChange={handleVariableChange}
          onDataChange={() => {
            // The preview is the explorer's own business; the fetch re-reads the
            // case data server-side, so nothing the browser loaded is sent.
          }}
        />
      </Box>
      <TextField
        size="small"
        label="Case filter (optional)"
        placeholder="e.g. age(1-3)"
        value={request.filter || ''}
        onChange={e => onChange({ ...request, filter: e.target.value })}
        disabled={disabled}
        inputProps={{ 'data-testid': 'autocoder-carbon-config-filter' }}
        sx={{ mt: 2, flexShrink: 0 }}
        helperText="Carbon filter syntax. Leave empty to import every case that answered."
      />
    </Box>
  )
}

export default CarbonConfig
